type MoveDirection = 'up' | 'down';

const EDITOR_SELECTOR = '[data-protocol-editor]';
const LIST_SELECTOR = '[data-protocol-list]';
const ITEM_SELECTOR = '[data-protocol-item]';

function items(list: Element): HTMLElement[] {
  return Array.from(list.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
}

export function syncProtocolOrder(list: Element): void {
  const orderedItems = items(list);
  orderedItems.forEach((item, index) => {
    const order = index + 1;
    const input = item.querySelector<HTMLInputElement>('[data-protocol-order]');
    const code = item.querySelector<HTMLInputElement>('[data-practice-code]');
    const position = item.querySelector<HTMLElement>('[data-protocol-position]');
    const moveUp = item.querySelector<HTMLButtonElement>('[data-protocol-move="up"]');
    const moveDown = item.querySelector<HTMLButtonElement>('[data-protocol-move="down"]');

    if (input) input.value = String(order);
    if (input) input.name = `Practicas[${index}].Orden`;
    if (code) code.name = `Practicas[${index}].Codigo`;
    if (position) position.textContent = String(order);
    if (moveUp) moveUp.disabled = index === 0;
    if (moveDown) moveDown.disabled = index === orderedItems.length - 1;
    item.setAttribute('aria-posinset', String(order));
    item.setAttribute('aria-setsize', String(orderedItems.length));
  });
  const count = list.parentElement?.querySelector<HTMLElement>('[data-protocol-count]');
  if (count) count.textContent = `${orderedItems.length} pasos`;
}

function createPracticeItem(code: string, name: string, station: string): HTMLElement {
  const item = document.createElement('div');
  item.className = 'protocol-editor-item';
  item.draggable = true;
  item.setAttribute('data-protocol-item', '');
  item.dataset.practiceCode = code;

  const codeInput = document.createElement('input');
  codeInput.type = 'hidden';
  codeInput.value = code;
  codeInput.setAttribute('data-practice-code', '');
  item.append(codeInput);

  const orderInput = document.createElement('input');
  orderInput.type = 'hidden';
  orderInput.setAttribute('data-protocol-order', '');
  item.append(orderInput);

  const grip = document.createElement('span');
  grip.className = 'protocol-editor-grip';
  grip.title = 'Arrastrar para reordenar';
  grip.setAttribute('aria-hidden', 'true');
  grip.innerHTML = '<i class="ph ph-dots-six-vertical"></i>';
  item.append(grip);

  const position = document.createElement('span');
  position.className = 'protocol-editor-position';
  position.setAttribute('data-protocol-position', '');
  item.append(position);

  const copy = document.createElement('div');
  copy.className = 'protocol-editor-practice';
  const title = document.createElement('strong');
  title.textContent = name;
  const detail = document.createElement('small');
  detail.textContent = `${station} · obligatoria`;
  copy.append(title, detail);
  item.append(copy);

  const controls = document.createElement('span');
  controls.className = 'protocol-editor-controls';
  controls.innerHTML = '<button type="button" data-protocol-move="up" aria-label="Subir práctica" title="Subir"><i class="ph ph-arrow-up"></i></button><button type="button" data-protocol-move="down" aria-label="Bajar práctica" title="Bajar"><i class="ph ph-arrow-down"></i></button><button type="button" data-protocol-remove aria-label="Quitar práctica del circuito" title="Quitar práctica"><i class="ph ph-trash"></i></button>';
  item.append(controls);
  return item;
}

export function moveProtocolItem(item: HTMLElement, direction: MoveDirection): boolean {
  const list = item.closest<HTMLElement>(LIST_SELECTOR);
  if (!list) return false;

  const sibling = direction === 'up'
    ? item.previousElementSibling
    : item.nextElementSibling;
  if (!(sibling instanceof HTMLElement) || !sibling.matches(ITEM_SELECTOR)) return false;

  if (direction === 'up') {
    list.insertBefore(item, sibling);
  } else {
    list.insertBefore(sibling, item);
  }
  syncProtocolOrder(list);
  return true;
}

function dragInsertionPoint(list: HTMLElement, pointerY: number): HTMLElement | null {
  const candidates = items(list).filter(item => !item.classList.contains('is-dragging'));
  let closest: { offset: number; item: HTMLElement } | null = null;

  candidates.forEach(item => {
    const rect = item.getBoundingClientRect();
    const offset = pointerY - rect.top - rect.height / 2;
    if (offset >= 0 || (closest && offset <= closest.offset)) return;
    closest = { offset, item };
  });
  return closest ? (closest as { offset: number; item: HTMLElement }).item : null;
}

function initEditor(editor: HTMLElement): void {
  if (editor.dataset.protocolEditorReady === 'true') return;
  editor.dataset.protocolEditorReady = 'true';

  const list = editor.querySelector<HTMLElement>(LIST_SELECTOR);
  if (!list) return;
  syncProtocolOrder(list);

  editor.querySelectorAll<HTMLInputElement>('[data-practice-toggle]').forEach(toggle => {
    toggle.addEventListener('change', () => {
      const code = toggle.value;
      const existing = list.querySelector<HTMLElement>(`${ITEM_SELECTOR}[data-practice-code="${CSS.escape(code)}"]`);
      if (toggle.checked && !existing) {
        list.append(createPracticeItem(code, toggle.dataset.practiceName ?? code, toggle.dataset.practiceStation ?? 'Clínica'));
      } else if (!toggle.checked && existing) {
        existing.remove();
      }
      syncProtocolOrder(list);
    });
  });

  list.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const remove = target?.closest<HTMLButtonElement>('[data-protocol-remove]');
    const removeItem = remove?.closest<HTMLElement>(ITEM_SELECTOR);
    if (remove && removeItem) {
      const code = removeItem.dataset.practiceCode
        ?? removeItem.querySelector<HTMLInputElement>('[data-practice-code]')?.value;
      removeItem.remove();
      if (code) {
        const toggle = editor.querySelector<HTMLInputElement>(`[data-practice-toggle][value="${CSS.escape(code)}"]`);
        if (toggle) toggle.checked = false;
      }
      syncProtocolOrder(list);
      return;
    }
    const button = target?.closest<HTMLButtonElement>('[data-protocol-move]');
    const item = button?.closest<HTMLElement>(ITEM_SELECTOR);
    const direction = button?.dataset.protocolMove as MoveDirection | undefined;
    if (!button || !item || (direction !== 'up' && direction !== 'down')) return;

    if (moveProtocolItem(item, direction)) {
      button.focus({ preventScroll: true });
      item.classList.add('was-moved');
      window.setTimeout(() => item.classList.remove('was-moved'), 450);
    }
  });

  list.addEventListener('dragstart', event => {
    const item = event.target instanceof Element
      ? event.target.closest<HTMLElement>(ITEM_SELECTOR)
      : null;
    if (!item) return;
    item.classList.add('is-dragging');
    event.dataTransfer?.setData('text/plain', 'protocol-practice');
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  });

  list.addEventListener('dragover', event => {
    const dragging = list.querySelector<HTMLElement>(ITEM_SELECTOR + '.is-dragging');
    if (!dragging) return;
    event.preventDefault();
    const before = dragInsertionPoint(list, event.clientY);
    if (before) list.insertBefore(dragging, before);
    else list.appendChild(dragging);
  });

  list.addEventListener('drop', event => {
    if (!list.querySelector(ITEM_SELECTOR + '.is-dragging')) return;
    event.preventDefault();
    syncProtocolOrder(list);
  });

  list.addEventListener('dragend', () => {
    const dragging = list.querySelector<HTMLElement>(ITEM_SELECTOR + '.is-dragging');
    dragging?.classList.remove('is-dragging');
    syncProtocolOrder(list);
  });
}

export function initProtocolEditors(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>(EDITOR_SELECTOR).forEach(initEditor);
}

function initialize(): void {
  initProtocolEditors();
  window.addEventListener('profint:navigation:ready', event => {
    const root = (event as CustomEvent<{ root?: ParentNode }>).detail?.root;
    initProtocolEditors(root ?? document);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
